import { TileMap, TileSize } from "../models"
import { classifyWaterDepth } from "./water_edges"

/** Ordered upstream to downstream, in tile coordinates. Width is the local half-width in tiles. */
export interface RiverPoint { x: number; y: number; width: number }
export interface RiverRock { x: number; y: number; id: string }
export interface RiverDefinition { points: RiverPoint[]; seed: number; rocks?: RiverRock[]; branches?: RiverPoint[][] }

/** Closest centreline segment, retaining its downstream direction and interpolated width. */
export const riverSample = (river: RiverDefinition, x: number, y: number) => {
    let distance = Infinity, dx = 0, dy = 1, width = 1, side = 0, along = 0
    for (const points of [river.points, ...(river.branches ?? [])]) {
        let travelled = 0
        for (let i = 1; i < points.length; i++) {
            const a = points[i - 1], b = points[i]
            const vx = b.x - a.x, vy = b.y - a.y, length = Math.hypot(vx, vy)
            if (length < 0.001) continue
            const projected = ((x - a.x) * vx + (y - a.y) * vy) / (length * length)
            const t = Math.max(0, Math.min(1, projected))
            const rx = x - a.x - t * vx, ry = y - a.y - t * vy, d = Math.hypot(rx, ry)
            const station = travelled + projected * length
            travelled += length
            if (d >= distance) continue
            along = station
            distance = d; dx = vx / length; dy = vy / length
            width = a.width + (b.width - a.width) * t
            side = rx * -dy + ry * dx
        }
    }
    return { distance, dx, dy, width, side, along }
}

/** Deterministic asymmetry gives broad gravel shelves opposite steeper cut banks. */
export const riverBankHeight = (river: RiverDefinition, px: number, py: number): number =>
    3 + Math.round(8 * (0.5 + 0.5 * Math.sin(px / 93 + Math.sin(py / 137 + river.seed) * 2.1)))

export const riverWaterLayer = (river: RiverDefinition, w: number, h: number): TileMap => {
    const wet = (x: number, y: number) => {
        const s = riverSample(river, x + .5, y + .5)
        const shelf = .18 * Math.sin(x * .53 + y * .31 + river.seed) + .12 * Math.sin(y * .77 - x * .23)
        return s.distance <= Math.max(.65, s.width + shelf)
    }
    const cells = classifyWaterDepth(wet, w, h)
    return Array.from({ length: w }, (_, x) => Array.from({ length: h }, (_, y) => String(cells[y * w + x])))
}

/** Pixel-space current; slow at shelves, faster in narrow reaches and down slopes. */
export const riverCurrent = (river: RiverDefinition, px: number, py: number, depth: number,
    downhill = 0, s = riverSample(river, px / TileSize, py / TileSize)): [number, number] => {
    const speed = Math.min(.95, (.24 + .65 / Math.max(1, s.width) + Math.max(0, downhill) * .35) *
        (.25 + .75 * Math.min(1, depth / 22)))
    // Gentle transverse circulation leaves the main current continuous through bends.
    const curl = Math.sin(px / 39 + py / 57 + river.seed) * .025 * Math.min(1, depth / 12)
    return [s.dx * speed - s.dy * curl, s.dy * speed + s.dx * curl]
}

/** A large boulder's base blocks the same two/three cells as its beach counterpart. Medium stones stay scenery. */
export const riverRockBlockingCells = (rock: RiverRock): Array<{ x: number; y: number }> => {
    if (!/^beach-boulder-[a-d]$/.test(rock.id)) return []
    return Array.from({ length: rock.id === "beach-boulder-c" ? 3 : 2 }, (_, dx) => ({ x: rock.x + dx, y: rock.y }))
}
